import { Component, EventEmitter, Input, Output, OnChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../button/button.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LanguageExtended } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { SelectComponent } from '../../select/select.component';
import { CvOptions } from '../../../models/cv-options.model';
import { FIELD_VALIDATION, VALIDATION_VALUES } from '../../../../../../../libs/shared/src/lib/constants/validation-constants';
import { getLanguages } from '../../../../../../../libs/shared/src/lib/i18n/languages/index';

@Component({
  selector: 'app-languages-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent, TranslocoPipe, SelectComponent],
  templateUrl: './languages-form.component.html',
  styleUrls: ['./languages-form.component.scss'],
})
export class LanguagesFormComponent implements OnChanges, OnInit {
  @Input() editingLanguage: LanguageExtended | null = null;
  @Input() editingIndex: number | null = null;
  @Input() cvOptions: CvOptions | null = null;

  @Output() save = new EventEmitter<Partial<LanguageExtended>>();
  @Output() cancel = new EventEmitter<void>();

  languageForm: FormGroup;
  languages: string[] = [];
  languageLevels: string[] = [];
  languageOptions: { value: string; label: string }[] = [];
  languageLevelOptions: { value: string; label: string }[] = [];
  isLoading = false;

  // Expose validation constants to template
  readonly VALIDATION = FIELD_VALIDATION;

  constructor(private fb: FormBuilder, private translocoService: TranslocoService) {
    this.languageForm = this.createForm();
  }

  /**
   * Create the form with validation using SSOT validation constants
   */
  private createForm(): FormGroup {
    return this.fb.group({
      language: ['', [Validators.required, Validators.maxLength(FIELD_VALIDATION.LANGUAGE_NAME_MAX_LENGTH)]],
      level: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.updateOptionsFromInput();
  }

  /**
   * Updates language and level options from the cvOptions input
   * and prepares the options for the select components
   */
  updateOptionsFromInput(): void {
    if (this.cvOptions) {
      this.languages = this.cvOptions.languages || [];
      this.languageLevels = this.cvOptions.languageLevels || [];
    } else {
      // Fallback to default values if no options provided
      this.languages = [];
      this.languageLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    }

    // Get current language and language translations
    const currentLang = this.translocoService.getActiveLang() as 'sk' | 'en' | 'cz';
    const languageTranslations = getLanguages(currentLang);

    // Prepare options for select components with translations
    this.languageOptions = this.languages.map((languageKey) => ({
      value: languageKey,
      label: (languageTranslations.languages as any)[languageKey] || languageKey,
    }));

    this.languageLevelOptions = this.languageLevels.map((level) => ({
      value: level,
      label: level,
    }));
  }

  ngOnChanges(): void {
    // Update options when cvOptions input changes
    this.updateOptionsFromInput();

    // Update form values when editing language
    if (this.editingLanguage) {
      this.languageForm.patchValue({
        language: this.editingLanguage.language,
        level: this.editingLanguage.level,
      });
    } else {
      this.languageForm.reset();
    }
  }

  onSubmit(): void {
    if (this.languageForm.invalid) {
      return;
    }

    this.save.emit(this.languageForm.value);
  }

  onCancel(): void {
    this.languageForm.reset();
    this.cancel.emit();
  }
}
